/* @flow */
/*
 *  Copyright 2017 Adobe Systems Incorporated. All rights reserved.
 *  This file is licensed to you under the Apache License, Version 2.0 (the "License");
 *  you may not use this file except in compliance with the License. You may obtain a copy
 *  of the License at http://www.apache.org/licenses/LICENSE-2.0
 *
 *  Unless required by applicable law or agreed to in writing, software distributed under
 *  the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
 *  OF ANY KIND, either express or implied. See the License for the specific language
 *  governing permissions and limitations under the License.
 *
 */

import React, {Component} from 'react';
import {connect} from 'react-redux';
import {checkThor} from '../../../actions/AppsActions';
import {getFFCData, setErrorType, getCompanionAppFFCData, getVideoCardsData} from '../../../actions/CCDActions';
import './Main.scss';
import CCDUtil from '../../../utils/CCDUtil';
import AppsUtil from '../../../utils/AppsUtil';
import CSSUtil from '../../../utils/CSSUtil';
import {addVariableCampaign, setCampaignVariationMapWithFGTests} from '../../../utils/TestUtil';
import CompatibilityUtil from '../../../utils/CompatibilityUtil';
import {promotion, productsToRedirectFor, errorTypes} from '../../../constants/ccdConstants';
import Kaizen from '../DownloadScreen/Kaizen/Kaizen';
import ErrorState from '../ErrorState/ErrorState';
import Thor from '../DownloadScreen/Thor/Thor';
import Campaign from '../DownloadScreen/Campaign/Campaign';
import Url from '../../../utils/Url';
import CCDIngestAnalytics from '../../../analytics/CCDIngestAnalytics';
import ccdDtmAnalytics from '../../../analytics/CCDDTMAnalytics';
import DownloadOnMobileScreen from '../../DownloadOnMobile/DownloadOnMobile';
import CCDownloadPage from '../DownloadScreen/CCDownloadPage/CCDownloadPage';
import {thorDetectData, sendIngestLoggedOutPromotionExpEvent, imslibfail} from '../../../utils/CCDAnalyticsUtils';
import DeviceDetectionUtil from '../../../utils/DeviceDetectionUtil';

type Props = {
	history: Object,
	getFFCData: Function,
	getCompanionAppFFCData: Function,
	getVideoCardsData: Function,
	setErrorType: Function,
	checkThor: Function,
	environment: string,
	imsData: Object,
	locale: string,
	routing: boolean,
	routeSchema: Object,
	routeParams: Object,
	hasThor: boolean,
	intl: Object,
	ffcData: Object,
	videoData: Object,
	errorType: string,
	location: Object,
	companionFFCData: Object,
	preFetchedRestAPIResponses: Function,
	profile: ?String
};

const workflow = {
	thor: 'thor',
	kaizen: 'kaizen',
	osDeprecated: 'osDeprecated',
	osNotSupported: 'osNotSupported'
};

class Main extends Component<Props> {
	productDetails: Object;
	workflow: string;
	isRedirected: boolean;
	userSignedInEventSessionStorage: string;
	redirectInitiated: boolean;

	constructor(props) {
		super(props);
		AppsUtil.setLocale(this.props.locale);
		AppsUtil.intl = this.props.intl;
		AppsUtil.profile = props.profile;
		this.isRedirected = false;
		if (productsToRedirectFor.indexOf(this.props.routeParams.productId) > -1) {
			/**
			 * These are the old product ids of some products that got re-branded later.
			 * Need to redirect these to appropriate pages to avoid break where the might have been integrated.
			*/
			this.routeRedirect(this.props.routeParams.productId);
		}
		this.productDetails = CCDUtil.getProductsData(this.props.routeParams.productId);
		this.userSignedInEventSessionStorage = `cc-download-user-signed-in-info-${this.productDetails && this.productDetails.cachedSapCode}`;
		ccdDtmAnalytics.setLanguageField();
		if (AppsUtil.isLoggedOutCampaignExp && Url.getParam('id')) {
			addVariableCampaign(Url.getParam('id'));
			setCampaignVariationMapWithFGTests(Url.getParam('id'), Url.getParam('vid'));
		}
	}

	getAnalyticsSubscriptionType() {
		if (this.props.imsData.isSignedUser) {
			if (this.props.serviceLevel === 'CS_LVL_2') { // paid user has this service level based on CCD code
				return 'paid';
			}
			return 'free';
		}
		return '';
	}

	getAnalyticsProduct() {
		return (this.productDetails.type === 'application-plan' && this.productDetails.planId) ? this.productDetails.planId : this.productDetails.id;
	}

	componentWillUnmount() {
		if (document.querySelector('#root')) {
			document.querySelector('#root').style.overflow = '';
			document.querySelector('html').style.overflow = '';
		}
		// Before unmounting, set document title to "Adobe Creative Cloud"
		document.title = this.props.intl.formatMessage({id: 'apps.document.title'});
	}

	sendAnalytic(type: string, hasThor?: boolean, url: string) {
		const ccdIngestAnalytics = new CCDIngestAnalytics(this.props.environment);
		ccdIngestAnalytics.initAnalytics();
		let data = {};
		switch (type) {
			case 'privacyBannerClicked':
				// ccdIngestAnalytics.genericAnalyticEvent();
				break;
			case 'thorPresent':
				// ccdIngestAnalytics.genericAnalyticEvent();
				break;
			case 'thorNotPresent':
				// ccdIngestAnalytics.genericAnalyticEvent();
				break;
			case 'thordetect':
				data = CCDUtil.getCloneObject(thorDetectData);
				data.value = hasThor;
				break;
			case 'imslibfail':
				data = CCDUtil.getCloneObject(imslibfail);
				data.contentAction = url;
				break;
		}
		data.componentName = hasThor ? 'Thor' : 'Kaizen';
		data.productId = this.productDetails.id;
		return ccdIngestAnalytics.genericAnalyticEvent(data);
	}

	// Url redirecting to specific products given specific productId
	routeRedirect(productId) {
		const isStarter = this.props.routeParams.pageType === 'starter';
		this.isRedirected = true;

		const isPromotion = AppsUtil.isLoggedOutPromotionExp;
		const workflowUrlString = `${isStarter ? '/starter' : ''}${isPromotion ? '/promotion' : ''}${this.props.location.search}`;
		switch (productId) {
			case 'lccp':
				window.location = `/apps/download/lightroom${workflowUrlString}${(typeof this.props.location.search !== 'undefined' && this.props.location.search !== '') ? '&purchaseLrccRedirect=true' : '?purchaseLrccRedirect=true'}`;
				break;
			case 'experience-design':
				window.location = `/apps/download/xd${workflowUrlString}`;
				break;
			case 'project-felix':
				window.location = `/apps/download/dimension${workflowUrlString}`;
				break;
			case 'flash':
				window.location = `/apps/download/animate${workflowUrlString}`;
				break;
			case 'flash-builder':
				window.location = this.props.environment === 'stage' ? AppsUtil.getCCDGoURL(this.props.locale, 'https://www.adobe.com/go/flash_builder_download_link_stage')
					:	AppsUtil.getCCDGoURL(this.props.locale, 'https://www.adobe.com/go/flash_builder_download_link_prod');
				break;
			case 'muse':
				window.location.replace(AppsUtil.getCCDGoURL(this.props.locale, 'https://www.adobe.com/go/muse_eol_message'));
				break;
			case 'photoplan':
				if (AppsUtil.isLoggedOutPromotionExp && Url.getParam('workflow') === promotion.panel && this.props.hasThor) {
					this.isRedirected = false;
					this.hideTransitionLoader();
				}
				else {
					window.location = `/apps/download/lightroom-classic${workflowUrlString}${(typeof this.props.location.search !== 'undefined' && this.props.location.search !== '') ? '&photoplanRedirect=true' : '?photoplanRedirect=true'}`;
				}
				break;
		}
	}

	hideHeader() {
		const headerElm = document.querySelector('.loggedOutExp');
		if (headerElm && headerElm.style) {
			headerElm.style.display = 'none';
		}
	}

	showHeader() {
		let headerElm = document.querySelector('.loggedOutExp');
		if (headerElm && headerElm.style) {
			headerElm.style.display = 'flex';
		}
		else {
			requestAnimationFrame(() => {
				headerElm = document.querySelector('.loggedOutExp');
				if (headerElm && headerElm.style) {
					headerElm.style.display = 'flex';
				}
			});
		}
	}

	showFooter() {
		const footerElm = document.querySelector('.footer-container, .trailhead-footer-container');
		if (footerElm && footerElm.style) {
			footerElm.style.display = 'flex';
		}
	}

	hideTransitionLoader() {
		/*
			Bug fix cch-10509
			Not removing loader here for Campaign pages
		*/
		if (AppsUtil.isLoggedOutCampaignExp
			&& !DeviceDetectionUtil.isMobileOrTab()) {
			return;
		}
		CSSUtil.hideTransitionLoader();
	}

	componentDidMount() {
		if (document.querySelector('#root')) {
			document.querySelector('#root').style.overflow = 'hidden';
			document.querySelector('html').style.overflow = 'hidden';
		}
		if (AppsUtil.isLoggedOutPromotionExp) {
			if (window.privacyConsentEventTriggered) {
				sendIngestLoggedOutPromotionExpEvent(this.props.routeParams.productId, 'privacy-banner-accept-clicked');
			}
			else {
				document.addEventListener('privacyConsentEventTriggered', () => {
					sendIngestLoggedOutPromotionExpEvent(this.props.routeParams.productId, 'privacy-banner-accept-clicked');
				});
			}
		}
		window.isCCDMainMounted = true; /* Used in cchome-react for privacy consent event */

		if (!this.isRedirected) {
			this.fetchData();
		}
		CCDUtil.hideFooter();
	}

	// check thor presence and fetch ffc data if productId is valid.
	fetchData() {
		const {routeParams, imsData, ffcData, preFetchedRestAPIResponses, locale} = this.props;
		const {productId} = routeParams;
		/* Fetch Thor presence info */
		this.props.checkThor(preFetchedRestAPIResponses);
		if (this.productDetails) {
			AppsUtil.setImsData(imsData);
			if (!ffcData) {
				/* Fetch ffc data if not already fetched. ffc data is always needed irrespective of platform and workflow */
				this.props.getFFCData(this.productDetails.sapCode, productId, preFetchedRestAPIResponses);
			}
			if (AppsUtil.isMobileOrTab()) {
				if (AppsUtil.isLoggedOutCampaignExp) {
					this.props.getVideoCardsData(this.productDetails.sapCode, locale);
				}
				else {
					const companionAppCode = AppsUtil.getCompanionApp(productId);
					if (companionAppCode) {
						this.props.getCompanionAppFFCData(companionAppCode, productId, preFetchedRestAPIResponses);
					}
				}
			}
		}
		else {
			CCDUtil.hideFooter();
			this.hideTransitionLoader();
		}
	}

	sendUserSignedInDTMEvent() {
		if (window.cchome.util.storageAvailable('sessionStorage')) {
			if (this.props.hasThor && !sessionStorage.getItem(this.userSignedInEventSessionStorage)) {
				if (this.props.imsData.isSignedUser) {
					// user currently signed-in
					// check if he signed in on CCD page or was already signed-in before he came to CCD page
					ccdDtmAnalytics.reportDTMEvent('DownloadCenter:Download-SignedIn', CCDUtil.getDTMEventData(), 'state');
					sessionStorage.setItem(this.userSignedInEventSessionStorage, 'alreadySignedIn');
				}
				else {
					sessionStorage.setItem(this.userSignedInEventSessionStorage, 'freshSignIn');
				}
			}
		}
	}

	componentWillUpdate(nextProps: Object) {
		if (nextProps.ffcData && nextProps.ffcData !== null && nextProps.hasThor !== undefined) { // FFC and Thor present data is available
			CCDUtil.setDTMEventData(this.getAnalyticsProduct(), this.getAnalyticsSubscriptionType(), this.productDetails.id, this.props.locale);
			if (nextProps.ffcData !== this.props.ffcData || nextProps.hasThor !== this.props.hasThor) {
				// One of the 'FFC' or 'Thor present' data came in just now. This is to ensure that the event is sent only once
				AppsUtil.printTimeEndLog('page_load_time');
				this.sendUserSignedInDTMEvent();
				this.sendAnalytic('thordetect', nextProps.hasThor);

				// Send legacy Ingest page load event & thor present event for promotion workflow
				if (AppsUtil.isLoggedOutPromotionExp) {
					sendIngestLoggedOutPromotionExpEvent(this.props.routeParams.productId, 'ccpd-page-loaded');
					if (nextProps.hasThor) {
						sendIngestLoggedOutPromotionExpEvent(this.props.routeParams.productId, 'ccpd-thor-present');
					}
				}
			}
		}
	}

	handleIMSLibFail() {
		const redirectURL = `${window.location.origin}/errors/5XX.html`;
		const analyticsProm = this.sendAnalytic('imslibfail', this.props.hasThor, redirectURL);
		const redirectTimer = setTimeout(() => {
			window.location.href = redirectURL;
		}, 2000);
		if (analyticsProm && analyticsProm.then) {
			analyticsProm.then(() => {
				clearTimeout(redirectTimer);
				window.location.href = redirectURL;
			});
		}
	}

	componentDidUpdate() {
		const ccdIngestAnalytics = new CCDIngestAnalytics(this.props.environment);
		AppsUtil.setVisitorGuid(ccdIngestAnalytics.getVisitorGuid());

		// Handling redirect in case of imslib failed to load, will redirect to error page if thor present.
		if ((this.props.hasThor && (!window.adobeIMS || this.props.profile === 'IMS Token Failure')) && !this.redirectInitiated && !DeviceDetectionUtil.isMobileOrTab() && !DeviceDetectionUtil.isNotMacOrWin()) {
			this.redirectInitiated = true;
			this.handleIMSLibFail();
		}

		if (this.isDataAvailable() || (this.props.errorType === errorTypes.serviceDown)) {
			if (this.isDataAvailable() && !AppsUtil.isMobileOrTab() && (this.props.routeParams.productId === 'ccpp' || this.props.routeParams.productId === 'ccppn') && !CompatibilityUtil.isAppSupported(this.productDetails.sapCode) && !CompatibilityUtil.isOSDeprecated()) {
				this.routeRedirect('photoplan');
			}
			else {
				this.hideTransitionLoader();
				if (AppsUtil.hasFeature('temp_enable_tracking_pixel')
					&& this.isDataAvailable()
					&& this.productDetails.sapCode === 'SPRK') {
					/* eslint-disable */
					!function(s, a, e, v, n, t, z) {
						if (s.saq) {
							return;
						}
						n = s.saq = function() {
							n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments)
						};
						if (!s._saq) {
							s._saq = n;
						}
						n.push = n;
						n.loaded = !0;
						n.version = '1.0';
						n.queue = [];
						t = a.createElement(e);
						t.async = !0;
						t.integrity = 'sha256-bT0Dsir1uXhPA/LSL10NSI/WyyOYX181oT97yrLRIaY=';
						t.crossOrigin = 'anonymous';
						t.src = v;
						z = a.getElementsByTagName(e)[0];
						z.parentNode.insertBefore(t, z);
					}(window, document, 'script', 'https://tags.srv.stackadapt.com/events_adobe.js');

					/* eslint-enable */
					saq('ts', 'nCqlBLQIba2v8dHcOyI8mg');
				}
			}
			window.cchome.util.storageAvailable('sessionStorage') && sessionStorage.removeItem(this.userSignedInEventSessionStorage);
		}
	}

	// check thor, ffc (if needed) actions are successfull and data is available.
	isDataAvailable() {
		const {ffcData, companionFFCData, errorType, hasThor, routeParams} = this.props;
		const isMobileOrTab = AppsUtil.isMobileOrTab();
		if (AppsUtil.isLoggedOutCampaignExp) {
			/* For campaign experience FFC data is the only required data */
			return ffcData;
		}
		if (isMobileOrTab && AppsUtil.getCompanionApp(routeParams.productId)) {
			/**
			 * For campanion app workflow on mobile device,
			 * FFC data is compulsory, while,
			 * companionFFCData is not compulsory
			 */
			return ffcData && (companionFFCData || (errorType === errorTypes.companionAppDataUnavailable));
		}
		/**
		 * For the leftover cases (non-campaign cases), i.e.,
		 * Mobile device with no companion app, and,
		 * Desktop platform
		 */
		return ffcData
			&& (hasThor !== undefined || isMobileOrTab
				|| (DeviceDetectionUtil.isNotMacOrWin() && !AppsUtil.hasFeature('bypass_compatibility_check')));
	}

	renderWhatToShow() {
		const {hasThor} = this.props;

		// If app not available or app not supported show OS not supported error page. Ignore promotion panel thor case.
		if ((!AppsUtil.appsFFCProductList[this.productDetails.sapCode] || (!CompatibilityUtil.isAppSupported(this.productDetails.sapCode) && !CompatibilityUtil.isAnyOlderVersionSupported(this.productDetails.sapCode) && !AppsUtil.hasFeature('bypass_compatibility_check')))
			&& !(hasThor && AppsUtil.isLoggedOutPromotionExp && Url.getParam('workflow') === promotion.panel)) {
			this.workflow = workflow.osNotSupported;
			return <ErrorState {...this.props} errorType='os'/>;
		}

		// Send legacy Ingest creative cloud subsequent non starter workflow event for promotion workflow
		if (AppsUtil.isLoggedOutPromotionExp && hasThor && !(this.props.routeParams.pageType === 'starter')) {
			sendIngestLoggedOutPromotionExpEvent(this.props.routeParams.productId, 'creative-cloud-subsequent-non-starter');
		}

		// If thor presents show Thor page (except for Creative cloud app).
		if (hasThor && this.productDetails.sapCode !== 'KCCC') {
			this.workflow = workflow.thor;
			return <Thor {...this.props} />;
		}
		// All other cases show Kaizen.
		this.workflow = workflow.kaizen;
		return <Kaizen {...this.props} />;
	}

	isCCDSupportedOS() {
		return AppsUtil.isCCDSupportedOS() || AppsUtil.hasFeature('bypass_compatibility_check');
	}

	isLoggedOutStarterWorkflow() {
		return this.props.routeParams.pageType === 'starter';
	}

	render() {
		const isMobileOrTab = AppsUtil.isMobileOrTab();
		const domArr = [];
		if (this.props.hasThor !== undefined) {
			if (!this.productDetails) {
				/**
				 * This is the case where the data was not found in ccdConstants, indicating this product is not onboarded.
				 * Show page not found error page in this case
				 */
				return (
					<section key='appsContainer' id='apps-container' className='apps-container ccd'>
						<ErrorState {...this.props} errorType='pageNotFound'/>
					</section>
				);
			}
			else if (this.props.errorType === errorTypes.serviceDown) {
				/**
				 * This is the case where some blocking data (for eg: FFC) is not available due to a network call failure
				 * Show "we are experiencing some issues" error page in this case
				 */
				return (
					<section key='appsContainer' id='apps-container' className='apps-container ccd'>
						<ErrorState {...this.props} errorType='service_down'/>
					</section>
				);
			}

			if (AppsUtil.isLoggedOutCampaignExp) {
				this.showHeader();
				return (
					<section key='appsContainer' id='apps-container' className='apps-container ccd'>
						<Campaign
							{...this.props}
							isDataAvailable={this.isDataAvailable()}
							isMobileOrTab={isMobileOrTab}/>
					</section>
				);
			}
			else if (AppsUtil.hasFeature('temp_cch_8523_trailhead') && this.productDetails.sapCode === 'KCCC' && !this.isLoggedOutStarterWorkflow()) {
				this.showHeader();
				if (!CompatibilityUtil.isAppSupported('KCCC') && !CompatibilityUtil.isAnyOlderVersionSupported('KCCC') && !AppsUtil.hasFeature('bypass_compatibility_check')) {
					return (
						<section key='appsContainer' id='apps-container' className='apps-container ccd'>
							<ErrorState {...this.props} errorType='os'/>
						</section>
					);
				}
				// In case of revamped cc download page, show trailhead footer
				this.showFooter();
				return (
					<section key='appsContainer' id='apps-container' className='apps-container ccd'>
						<CCDownloadPage
							{...this.props}
							productDetails={this.productDetails}
							isDataAvailable={this.isDataAvailable()}
						/>
					</section>
				);
			}
			else if (isMobileOrTab) { // Email me link page if mobile or tab
				return this.isDataAvailable() ? (
					<section key='appsContainer' id='apps-container' className='apps-container ccd'>
						<DownloadOnMobileScreen {...this.props} process='ccd' formatMessage={this.props.intl.formatMessage} />
					</section>
				) : null;
			}

			if (this.isCCDSupportedOS() && (
				!CompatibilityUtil.isOSDeprecated()
					|| (this.productDetails.sapCode === 'KCCC')
					|| (AppsUtil.isLoggedOutPromotionExp && Url.getParam('workflow') === promotion.panel)
			)) {
				domArr.push((
					<section key='appsContainer' id='apps-container' className='apps-container ccd'>
						{this.isDataAvailable() ? this.renderWhatToShow() : null}
					</section>
				));
				if (workflow.kaizen === this.workflow) {
					this.hideHeader();
				}
				else {
					this.showHeader();
				}
			}
			else {
				domArr.push(<section key='appsContainer' id='apps-container' className='apps-container ccd'>
					<ErrorState {...this.props} errorType='os'/>
				</section>);
			}
		}
		return (
			domArr
		);
	}
}

function mapStateToProps(state) {
	return {
		ffcData: state.ccd.ffcData,
		videoData: state.ccd.videoData,
		companionFFCData: state.ccd.companionFFCData,
		hasThor: state.apps.hasThor,
		errorType: state.ccd.errorType
	};
}

export default connect(mapStateToProps, {getFFCData, checkThor, setErrorType, getCompanionAppFFCData, getVideoCardsData})(Main);
